(ns practitest.frontend.common.assistant-service
  (:require [ajax.core :as ajax]
            [clojure.string :as str]
            [practitest.frontend.common.fetch :refer [fetch-json]]
            [practitest.frontend.common.i18n :refer [format-translation
                                                     get-translation]]
            [practitest.frontend.common.re-frame :refer [build-event-vector]]
            [practitest.frontend.device-connection.stream :as stream]
            [practitest.frontend.routing.backend-routes :as broutes]
            [practitest.frontend.subs :as subs]
            [re-frame.core :as re-frame]))

(defn get-token [project-id]
  (-> (fetch-json (broutes/get-url "service_tokens#create" {:p_id project-id})
                  {:method "POST"})
      (.then (fn [{:keys [ok? status body]}]
               (if ok?
                 (:token body)
                 (throw (ex-info "Couldn't get service token" {:status status})))))))

(defn check-budget [project-id]
  (-> (fetch-json (broutes/get-url "assistant#budget" {:p_id project-id}) {})
      (.then (fn [{:keys [ok? body status]}]
               (if ok?
                 body
                 (throw (ex-info "Couldn't check budget" {:status status})))))
      (.catch (fn [_err]
                {:allowed true :status "ok"}))))

;; Requests map (global)
(defonce ^:private in-flight (atom {}))

;; TODO: Do we really need this here ?
(def response-timeout 120000)

;;
;; Request-response model over streaming HTTP connection
;; Looks like a workaround, but it solves a couple of issues with current rails backend:
;; 1. Worker starvation on rails backend
;; 2. Supports budget checks and usage reporting
;; 3. Reuses existing assistant service
;;
(re-frame/reg-fx
  ::request
  (fn [{:keys [id project-id mode
               assistant-service-url messages
               on-buffered-token
               on-tool-calls
               on-success
               on-failure
               on-request
               tools] :as request}]
    (assert (some? id))
    (assert (some? project-id))
    (assert (some? mode))

    (when-some [stream (:stream (get @in-flight id))]
      (js/console.warn "Aborting in-flight request" id)
      (stream/disconnect! stream))

    (let [marker (js/Object.)]
      (swap! in-flight assoc id {:marker marker})
      (-> (check-budget project-id)
          (.then (fn [budget]
                   (when (= "no_consent" (:status budget))
                     (throw (ex-info (get-translation "test.smartfox.no-ai-consent-tooltip"
                                                      "Generative AI consent is needed for this account")
                                     {})))
                   (when (not (:allowed budget))
                     (throw (ex-info (get-translation "assistant.budget.hard-limit"
                                                      "Monthly usage limit reached. Contact your account admin.")
                                     {})))))
          (.then #(get-token project-id))
          (.then (fn [token]
                   (js/Promise.
                     (fn [resolve reject]
                       (let [conn-holder (atom nil)
                             text-buffer (atom "")
                             usage (atom nil)
                             timeout-id (js/setTimeout
                                          (fn []
                                            (some-> @conn-holder stream/disconnect!)
                                            (reject (ex-info "Timed out" {})))
                                          response-timeout)
                             finish! (fn [f v]
                                       (js/clearTimeout timeout-id)
                                       (some-> @conn-holder stream/disconnect!)
                                       (f v))
                             conn (stream/connect!
                                    {:base-url assistant-service-url
                                     :headers {"Authorization" (str "Bearer " token)}
                                     :on-open (fn [conn]
                                                (when on-request
                                                  (re-frame/dispatch (build-event-vector on-request conn)))
                                                (-> (stream/send! conn {:type "init"
                                                                        :mode mode
                                                                        ;; Required on pt-services side for now, but can be empty
                                                                        :fields []
                                                                        :tools (or tools [])})
                                                    (.then #(stream/send! conn
                                                                          {:type "chat"
                                                                           :messages messages}))
                                                    (.catch #(finish! reject %))))
                                     :on-message (fn [{:keys [type] :as msg}]
                                                   (case type
                                                     "token" (do
                                                               (swap! text-buffer str (:content msg))
                                                               (when on-buffered-token
                                                                 (re-frame/dispatch
                                                                   (build-event-vector on-buffered-token @text-buffer))))
                                                     "usage" (do (reset! usage msg)
                                                                 (re-frame/dispatch [::report-usage msg]))
                                                     "tool-calls" (when on-tool-calls
                                                                    (re-frame/dispatch
                                                                      (build-event-vector
                                                                        on-tool-calls (:calls msg))))
                                                     "error" (finish! reject (ex-info (:message msg)
                                                                                      {}))
                                                     "done" (finish! resolve {:content @text-buffer
                                                                              :usage @usage})
                                                     nil))
                                     :on-close (fn [e]
                                                 (finish! reject e))
                                     :on-error (fn [e]
                                                 (finish! reject e))})]
                         (reset! conn-holder conn)
                         (swap! in-flight assoc-in [id :stream] conn))))))
          (.then (fn [result]
                   (when on-success
                     (re-frame/dispatch (build-event-vector on-success (:content result))))))
          (.catch (fn [err]
                    (re-frame/dispatch
                      (build-event-vector
                        on-failure {:aborted? (stream/aborted-error? err)
                                    :message (ex-message err)}))))
          (.finally (fn []
                      (when-let [data (get @in-flight id)]
                        (when (identical? marker (:marker data))
                          (swap! in-flight dissoc id)
                          (some-> (:stream data) (stream/disconnect!))))))))))

(re-frame/reg-fx
  ::abort
  (fn [conn]
    (stream/disconnect! conn)))

;; For logging purposes
(re-frame/reg-event-fx
  ::log
  (fn [_ _]
    {}))

(re-frame/reg-event-fx
  ::report-usage
  (fn [{db :db} [_ {:keys [prompt_tokens completion_tokens conversation-id model mode byok]}]]
    (let [project-id (subs/get-current-project-id db)]
      {:http-xhrio {:uri (broutes/get-url "assistant#usage" {:p_id project-id})
                    :method :post
                    :format (ajax/json-request-format)
                    :response-format (ajax/raw-response-format)
                    :on-success [::log]
                    :on-failure [::log]
                    :params {:prompt_tokens prompt_tokens
                             :completion_tokens completion_tokens
                             :conversation_id conversation-id
                             :model model
                             :mode mode
                             :byok byok}}})))

(defn- strip-json-fence
  "Removes the ```json ... ``` fence LLMs sometimes wrap JSON responses in."
  [s]
  (some-> s
          (str/replace-first #"(?m)^```json\n*" "")
          (str/replace-first #"\n*```$" "")))

(defn parse-model-json
  [s on-failure]
  (when s
    (try
      (js->clj (js/JSON.parse (strip-json-fence s))
               :keywordize-keys true)
      (catch js/Error e
        (when on-failure
          (re-frame/dispatch (build-event-vector on-failure)))
        nil))))
